import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { selfexclusion } from "../signIn/cl_SignInSlice";
import errorCodes from "../../api/errorCodes";

const SelfExclusion = () => {
  const dispatch = useDispatch();
  const [limitType, setLimitType] = useState("self-exclusion");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [depositPeriod, setDepositPeriod] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [bettingPeriod, setBettingPeriod] = useState("");
  const [bettingAmount, setBettingAmount] = useState("");
  const [timePeriod, setTimePeriod] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSave = () => {
    if (limitType === "self-exclusion") {
      if (!selectedPeriod) {
        setErrorMessage("Please select a self-exclusion period");
        return;
      }
      setShowConfirmation(true);
    } else if (limitType === "deposit") {
      if (!depositPeriod) {
        setErrorMessage("Please select a deposit limit period");
        return;
      }
      if (!depositAmount) {
        setErrorMessage("Please enter a deposit limit amount");
        return;
      }
      processSave("deposit", depositPeriod);
    } else if (limitType === "betting") {
      if (!bettingPeriod) {
        setErrorMessage("Please select a betting limit period");
        return;
      }
      if (!bettingAmount) {
        setErrorMessage("Please enter a betting limit amount");
        return;
      }
      processSave("betting", bettingPeriod);
    } else if (limitType === "time") {
      if (!timePeriod) {
        setErrorMessage("Please select a time limit period");
        return;
      }
      processSave("time", timePeriod);
    }
  };

  const processSave = async (type, value) => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      let payload = {};

      if (type === "self-exclusion") {
        const selfExclusionMapping = {
          "WEEK": "WEEK",
          "MONTH": "MONTH",
          "YEAR": "YEAR",
        };
        payload = {
          selfExclusion: selfExclusionMapping[value],
        };
      } else if (type === "deposit") {
        if (!depositAmount) {
          setErrorMessage("Please enter a deposit limit amount");
          setIsLoading(false);
          return;
        }
        if (value === "daily") {
          payload = { dailyDepositLimit: Number(depositAmount) };
        } else if (value === "weekly") {
          payload = { weeklyDepositLimit: Number(depositAmount) };
        } else if (value === "monthly") {
          payload = { monthlyDepositLimit: Number(depositAmount) };
        }
      } else if (type === "betting") {
        if (!bettingAmount) {
          setErrorMessage("Please enter a betting limit amount");
          setIsLoading(false);
          return;
        }
        if (value === "daily") {
          payload = { dailyStakeLimit: Number(bettingAmount) };
        } else if (value === "weekly") {
          payload = { weeklyStakeLimit: Number(bettingAmount) };
        } else if (value === "monthly") {
          payload = { monthlyStakeLimit: Number(bettingAmount) };
        }
      } else if (type === "time") {
        const timeMapping = {
          "15min": 15,
          "30min": 30,
          "1hr": 60,
          "2hrs": 120,
          "4hrs": 240,
          "8hrs": 480
        };
        payload = { timeLimit: timeMapping[value] };
      }

      if (payload && Object.keys(payload).length > 0) {
        try {
          const result = await dispatch(selfexclusion(payload));
          if (result?.success) {
            setShowSuccessMessage(true);
            setShowConfirmation(false);
          }
          else{
             const friendlyError = getErrorMessage(result?.data || "USERLIMITS_UPDATE_ERROR");
             setErrorMessage(friendlyError);
          }
          setTimeout(() => {
            setShowSuccessMessage(false);
            handleReset();
          }, 10000);
        } catch (apiError) {
          // Extract error message from the thrown error
          const errorCode = apiError?.message || apiError?.errorCode || "USERLIMITS_UPDATE_ERROR";
          const friendlyError = getErrorMessage(errorCode);
          setErrorMessage(friendlyError);
          console.error("API Error:", errorCode);
        }
      } else {
        setErrorMessage("Invalid selection. Please try again.");
      }
    } catch (error) {
      // Extract error message from the thrown error
      const errorCode = error?.message || error?.error || "USERLIMITS_UPDATE_ERROR";
      const friendlyError = getErrorMessage(errorCode);
      setErrorMessage(friendlyError);
      console.error("Error:", errorCode);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedPeriod("");
    setDepositPeriod("");
    setDepositAmount("");
    setBettingPeriod("");
    setBettingAmount("");
    setTimePeriod("");
    setErrorMessage("");
    setShowConfirmation(false);
  };

  const handleConfirm = () => {
    processSave("self-exclusion", selectedPeriod);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleNumberInput = (value, setter) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setter(numericValue);
  };

  const getErrorMessage = (apiErrorCode) => {
    if (!apiErrorCode) {
      return "Failed to update limit. Please try again.";
    }

    const normalizedCode = String(apiErrorCode).replaceAll(" ", "_").toUpperCase();
    return errorCodes[normalizedCode] || apiErrorCode || "Failed to update limit. Please try again.";
  };

  return (
    <div className="SB-page-content">
      <div className="SB-myAccountSection-container SB-responsibleGambling">
        <div className="SB-pageHeader">
          <div className="SB-pageBackButton">
            <div className="SB-iconSVG">
              <svg xmlns="http://www.w3.org/2000/svg">
                <use xlinkHref="images/sprite-other-icons.svg#icon-back"></use>
              </svg>
            </div>
          </div>
          <div className="SB-pageHeader-title">Responsible Gaming</div>
        </div>

        <div className="SB-content">
          <div className="SB-form-group" style={{ marginBottom: "20px" }}>
            <label className="SB-form-label">Gambling Limit Type</label>
            <div className="SB-select">
              <select
                value={limitType}
                onChange={(e) => {
                  setLimitType(e.target.value);
                  handleReset();
                }}
              >
                <option value="self-exclusion">Self-Exclusion Limits</option>
                <option value="deposit">Deposit Limits</option>
                <option value="betting">Betting Limits</option>
                <option value="time">Time Limits</option>
              </select>
            </div>
          </div>

          <div className="SB-myAccountSection">
            <div className="SB-myAccountSection-header">
              {limitType === "self-exclusion" && "Self-Exclusion Limits"}
              {limitType === "deposit" && "Deposit Limits"}
              {limitType === "betting" && "Betting Limits"}
              {limitType === "time" && "Time Limits"}
            </div>

            <div className="SB-myAccountSection-content">
              {/* SELF-EXCLUSION SECTION */}
              {limitType === "self-exclusion" && (
                <div className="SB-form-group">
                  <label className="SB-form-label">Exclusion Period</label>
                  <div className="SB-select">
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="WEEK">Week</option>
                      <option value="MONTH">Month</option>
                      <option value="YEAR">Year</option>
                    </select>
                  </div>
                </div>
              )}

              {/* DEPOSIT LIMITS SECTION */}
              {limitType === "deposit" && (
                <>
                  <div className="SB-form-group">
                    <label className="SB-form-label">Time Limits</label>
                    <div className="SB-select">
                      <select
                        value={depositPeriod}
                        onChange={(e) => setDepositPeriod(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>
                  <div className="SB-form-group">
                    <div className="SB-form-control">
                      <input
                        type="text"
                        placeholder="Enter Limit"
                        value={depositAmount}
                        onChange={(e) => handleNumberInput(e.target.value, setDepositAmount)}
                      />
                      <label className="SB-form-label">Enter Limit</label>
                    </div>
                  </div>
                </>
              )}

              {/* BETTING LIMITS SECTION */}
              {limitType === "betting" && (
                <>
                  <div className="SB-form-group">
                    <label className="SB-form-label">Time Limits</label>
                    <div className="SB-select">
                      <select
                        value={bettingPeriod}
                        onChange={(e) => setBettingPeriod(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>
                  <div className="SB-form-group">
                    <div className="SB-form-control">
                      <input
                        type="text"
                        placeholder="Enter Limit"
                        value={bettingAmount}
                        onChange={(e) => handleNumberInput(e.target.value, setBettingAmount)}
                      />
                      <label className="SB-form-label">Enter Limit</label>
                    </div>
                  </div>
                </>
              )}

              {/* TIME LIMITS SECTION */}
              {limitType === "time" && (
                <div className="SB-form-group">
                  <label className="SB-form-label">Time Limits</label>
                  <div className="SB-select">
                    <select
                      value={timePeriod}
                      onChange={(e) => setTimePeriod(e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="15min">15 Min</option>
                      <option value="30min">30 Min</option>
                      <option value="1hr">1 Hr</option>
                      <option value="2hrs">2 Hrs</option>
                      <option value="4hrs">4 Hrs</option>
                      <option value="8hrs">8 Hrs</option>
                    </select>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="SB-form-errorMsg" style={{ marginBottom: "20px" }}>
                  <span>{errorMessage}</span>
                </div>
              )}

              {showSuccessMessage && (
                <div className="SB-msgBox SB-success" style={{ marginBottom: "20px" }}>
                  <div className="SB-msgBox-container">
                    {limitType === "self-exclusion" && "Self-exclusion has been applied successfully."}
                    {limitType === "deposit" && "Deposit limit has been saved successfully."}
                    {limitType === "betting" && "Betting limit has been saved successfully."}
                    {limitType === "time" && "Time limit has been applied successfully."}
                  </div>
                </div>
              )}

              <div className="SB-btnContainer text-center">
                <button
                  className="btn SB-btn SB-btnSecondary active SB-btnLarge"
                  onClick={handleSave}
                  disabled={isLoading || showConfirmation}
                >
                  {isLoading ? "Processing..." : "Save"}
                </button>
                <button
                  className="btn SB-btn SB-btnSecondary active SB-btnLarge SB-btnReset"
                  onClick={handleReset}
                  disabled={isLoading}
                >
                  Reset
                </button>
              </div>

              {showConfirmation && limitType === "self-exclusion" && (
                <>
                  <div className="SB-infoTxt" style={{ marginTop: "24px", marginBottom: "24px", backgroundColor: "rgba(0, 0, 0, 0.3)", padding: "16px" }}>
                    Do you want to go ahead with selected self-exclusion period. Please confirm.
                  </div>
                  <div className="SB-btnContainer text-center">
                    <button
                      className="btn SB-btn SB-btnSecondary active SB-btnLarge"
                      onClick={handleConfirm}
                      disabled={isLoading}
                    >
                      {isLoading ? "Processing..." : "Confirm"}
                    </button>
                    <button
                      className="btn SB-btn SB-btnSecondary active SB-btnLarge SB-btnReset"
                      onClick={handleCancelConfirmation}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {limitType === "self-exclusion" && (
                <div className="SB-infoTxt" style={{ marginTop: "24px" }}>
                  Self-Exclusion allows you to suspend your account and take some time away from
                  betting and playing with us for a selected period of time. During this time, you
                  will be able to log into your account and withdraw, but unable to bet or play on
                  the site.
                </div>
              )}

              {limitType === "deposit" && (
                <div className="SB-infoTxt" style={{ marginTop: "24px" }}>
                  Deposit limits give you the ability to stay in control of your money and how much you
                  want to spend. Once you reach your limit, you will be unable to deposit any more
                  funds.
                </div>
              )}

              {limitType === "betting" && (
                <div className="SB-infoTxt" style={{ marginTop: "24px" }}>
                  Betting limits give you the ability to stay in control of your money and how much you
                  want to spend. Once you reach your limit, you will be unable to place a bet on daily,
                  weekly or monthly limits on the preferred amount of money you want to wager.
                </div>
              )}

              {limitType === "time" && (
                <div className="SB-infoTxt" style={{ marginTop: "24px" }}>
                  Time limits allow you to take breaks during your session. A new setting will only
                  apply on the next session before 24 hours.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfExclusion;
